import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, from, map, catchError, switchMap, tap, delay, mergeMap, filter } from 'rxjs';
import { ZefAuthApi } from './auth.api';
import { ZefHttpErrorResponse } from '@zerops/zef/errors';
import { authActions } from './auth.state';
import { TokenData } from './auth.model';
import { urlSurvivesSessionChange } from './auth.url';
import {
  AuthTokenStorageService,
  performWebAuthnAuthentication,
  performWebAuthnRegistration,
  isSudoRequiredError,
  SUDO_ERROR_KEY
} from './auth.utils';
import { zefAddError } from '@zerops/zef/errors';
import { zefRemoveProgress } from '@zerops/zef/progress';
import { zefWebsocketTerminate } from '@zerops/zef/websocket/websocket.action';
import { loadUser, UserDataStorageService } from '@zerops/zerops/core/user-base';
import { PasswordChangeForm } from '@zerops/zerops/pages/+settings/pages/settings-account/modules';

@Injectable()
export class AuthEffect {
  #actions$ = inject(Actions);
  #api = inject(ZefAuthApi);
  #tokenStorage = inject(AuthTokenStorageService);
  #userDataStorage = inject(UserDataStorageService);
  #passwordChangeForm = inject(PasswordChangeForm);

  #initType = '[@zerops/zef/auth] effect-init';

  // # Core Auth Effects
  onLogin$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.login),
    switchMap((action) => this.#api
      .login$({
        ...action.data
      })
      .pipe(
        map((response) => authActions.loginSuccess(
          response.tokenData,
          action,
          response.response
        )),
        catchError((err) => of(authActions.loginFail(err, action)))
      )
    )
  ));

  onLoginSuccess$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.loginSuccess),
    map(({ data }) => authActions.setToken(data, authActions.loginSuccess.type))
  ));

  onRefreshTokenSuccessSetToken$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.refreshTokenSuccess),
    map(({ data }) => authActions.setToken(data, authActions.refreshTokenSuccess.type))
  ));

  onSetToken$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.setToken),
    tap(({ data }) => this.#tokenStorage.setToken(data))
  ), { dispatch: false });

  onInitTriggerTokenCheck$ = createEffect(() => this.#actions$.pipe(
    ofType(this.#initType),
    map(() => authActions.checkStoredToken(this.#tokenStorage.isValid()))
  ));

  /**
   * Validates stored authentication token on app initialization.
   *
   * This effect determines if the user should be automatically logged in
   * using a stored token from localStorage.
   *
   * Token validation rules:
   * - Token must exist in localStorage
   * - Token must have a valid accessToken field
   * - Token must NOT require 2FA verification (partial tokens are invalid)
   *
   * If a partial 2FA token is found (from incomplete login flow), it will
   * be treated as invalid and removed, redirecting the user to login page
   * where they can start a fresh authentication flow.
   *
   * This prevents the "blank page" bug where partial tokens cause:
   * 1. authState to be set to Authorized incorrectly
   * 2. loadUser() to fail with unverified token
   * 3. Permission guards to fail due to null activeClientUserId
   * 4. Router unable to navigate anywhere
   * 5. The userId in the token and the user data must match if both exist.
   *
   * Cross-tab user change detection:
   * sessionStorage is per-tab and survives F5 — unlike the in-memory
   * sessionInvalidated signal which resets on reload. If a different user
   * logged in from another tab, the localStorage token belongs to the new user
   * but the current URL may reference resources the new user cannot access.
   * Redirect to a safe URL instead of loading a resource that would 403 —
   * except on a URL that names no such resource, where the redirect is pure
   * loss (`urlSurvivesSessionChange`). `/authorize-app` is the case that
   * matters: the hand-over request exists only in that URL and cannot be
   * restarted from the dashboard.
   */
  onCheckToken$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.checkStoredToken),
    map(({ valid }) => {
      if (valid) {
        const token = this.#tokenStorage.getToken();

        // Read once: both session-change branches below ask the same question
        // of the same, stable value.
        const urlSurvives = urlSurvivesSessionChange(window.location.pathname);

        const previousUserId = this.#tokenStorage.getSessionUserId();
        if (
          previousUserId
          && previousUserId !== token.userId
          && !urlSurvives
        ) {
          this.#tokenStorage.setSessionUserId(token.userId);
          window.location.replace('/dashboard/projects');
          return authActions.noopAction();
        }

        this.#tokenStorage.setSessionUserId(token.userId);

        // Cross-tab organization change detection:
        // Same user but different clientUserId means another tab switched orgs.
        // Redirect to safe URL to avoid showing data from the wrong organization.
        const userData = this.#userDataStorage.getData();
        const previousClientUserId = this.#tokenStorage.getSessionClientUserId();
        if (
          previousClientUserId
          && userData?.clientUserId
          && previousClientUserId !== userData.clientUserId
          && !urlSurvives
        ) {
          this.#tokenStorage.setSessionClientUserId(userData.clientUserId);
          window.location.replace('/dashboard/projects');
          return authActions.noopAction();
        }
        if (userData?.clientUserId) {
          this.#tokenStorage.setSessionClientUserId(userData.clientUserId);
        }

        if (!!userData?.userId && token.userId !== userData.userId) {
          this.#userDataStorage.removeData();
          return authActions.checkStoredTokenFail();
        } else {
          return authActions.checkStoredTokenSuccess(token);
        }
      } else {
        // Token is invalid - either missing, malformed, or requires 2FA
        // Token will be removed and user redirected to login page
        return authActions.checkStoredTokenFail();
      }
    })
  ));

   /**
   * It is necessary to distinguish a special case, when the logout action is used
   * for clearing the state and notify BE before processing the /authorize route.
   * It is used mainly when opening different clients through the BackOffice application.
   */
  onLogout$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.logout),
    map((action) => authActions.apiLogout(action?.meta))
  ));

  onCheckStoredTokenFail$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.checkStoredTokenFail, authActions.refreshTokenFail),
    filter(() => !this.#tokenStorage.sessionInvalidated()),
    tap(() => this.#tokenStorage.removeToken())
  ), { dispatch: false });

  onCrossTabSessionInvalidated$ = createEffect(() =>
    this.#tokenStorage.crossTabSessionInvalidated$.pipe(
      map(() => zefWebsocketTerminate())
    )
  );

  onApiLogoutRemoveStoredToken$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.apiLogout),
    // Skip token removal for BackOffice authorize flow to avoid
    // triggering cross-tab logout in other windows
    filter(({ meta }) => !meta?.preserveStorageToken),
    // delay needed to let api trigger with auth header
    delay(50),
    tap(() => this.#tokenStorage.removeToken())
  ), { dispatch: false });

  onApiLogout$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.apiLogout),
    switchMap((action) => {

      /**
     * It is necessary to distinguish a special case in which the logout action is used
     * to clear the state. BE should not be notified that the actual accessToken and
     * refreshToken have been invalidated on the FE side, because the newly opened window
     * was invoked via a URL from BO, and secrets were passed via query parameters.
     *
     * The reason is that the previously opened window can continue to send periodic
     * requests (statistics, etc.), and those requests would end with a 401 error.
     *
     * This error code invokes refreshing the accessToken, which fails. The @zerops/zef/auth
     * key is cleared from local storage, and this affects other windows of the Zerops
     * application on the same domain.
     *
     * It is used mainly when opening different clients through the BackOffice application.
     */
      if (action?.meta?.redirect === false) {
        return of().pipe(
          map(() => authActions.noopAction())
        );
      }

      return this.#api.logout$().pipe(
        map(() => authActions.apiLogoutSuccess()),
        catchError(() => of(authActions.apiLogoutFail()))
      )

    })
  ));

  logoutEverywhere$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.logoutEverywhere),
    switchMap((action) =>
      this.#api.logoutEverywhere$().pipe(
        map((response) => authActions.logoutEverywhereSuccess(response, action)),
        catchError((error) => of(authActions.logoutEverywhereFailure(error, action)))
      )
    )
  ));

  // # 2FA Login Effects
  onTOTPVerify$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.verifyTOTPCode),
    switchMap((action) => this.#api.totpLogin$(action.data.token).pipe(
      map((response) => authActions.verifyTOTPCodeSuccess(response, action)),
      catchError((error) => of(authActions.verifyTOTPCodeFail(error, action)))
    ))
  ));

  onTOTPVerifySuccess$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.verifyTOTPCodeSuccess),
    mergeMap(({ data }) => [
      authActions.loginSuccess(data.auth, authActions.verifyTOTPCodeSuccess),
      authActions.setToken(data.auth, authActions.verifyTOTPCodeSuccess.type)
    ])
  ));

  onU2FVerifyInitiate$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.initiateU2FVerify),
    switchMap((action) => this.#api.twoFALoginStart$().pipe(
      map((challenge) => authActions.initiateU2FVerifySuccess({ challenge }, action)),
      catchError((error) => of(authActions.initiateU2FVerifyFail(error, action)))
    ))
  ));

  onU2FVerifySuccess$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.initiateU2FVerifySuccess),
    switchMap(({ data }) =>
      performWebAuthnAuthentication({
        challenge: data.challenge.response,
        timeout: 60000
      }).pipe(
        map((response) => authActions.submitU2FResponse({
          data: {
            sessionId: data.challenge.sessionId,
            response
          }
        })),
        catchError((error) => {
          return of(authActions.submitU2FResponseFail(error, authActions.initiateU2FVerify()));
        })
      )
    )
  ));

  onU2FResponseSubmit$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.submitU2FResponse),
    switchMap((action) => this.#api.twoFALoginFinish$(action.data).pipe(
      map((response) => authActions.submitU2FResponseSuccess(response, action)),
      catchError((error) => of(authActions.submitU2FResponseFail(error, action)))
    ))
  ));

  onSubmitU2FResponseSuccess$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.submitU2FResponseSuccess),
    mergeMap(({ data }) => [
      authActions.loginSuccess(data.auth, authActions.submitU2FResponseSuccess),
      authActions.setToken(data.auth, authActions.submitU2FResponseSuccess.type)
    ])
  ));

  // Passkey login start for conditional UI (autofill)
  // Triggered automatically on login page load
  onPasskeyLoginStart$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.passkeyLoginStart),
    switchMap((action) => this.#api.passkeyLoginStart$().pipe(
      map((challenge) => authActions.passkeyLoginStartSuccess({ challenge }, action)),
      catchError((error) => of(authActions.passkeyLoginStartFail(error, action)))
    ))
  ));

  // Passkey login start for manual user-initiated flow
  // Triggered when user explicitly clicks "Sign in with passkey" button
  onManualPasskeyLogin$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.manualPasskeyLogin),
    switchMap((action) => this.#api.passkeyLoginStart$().pipe(
      map((challenge) => authActions.manualPasskeyLoginSuccess({ challenge }, action)),
      catchError((error) => of(authActions.manualPasskeyLoginFail(error, action)))
    ))
  ));

  onPasskeyLoginCompleteSubmit$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.passkeyLoginComplete),
    switchMap((action) => this.#api.passkeyLoginFinish$(action.data).pipe(
      map((response) => authActions.passkeyLoginCompleteSuccess(response, action)),
      catchError((error) => of(authActions.passkeyLoginCompleteFail(error, action)))
    ))
  ));

  onPasskeyLoginCompleteSuccess$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.passkeyLoginCompleteSuccess),
    mergeMap(({ data }) => [
      authActions.loginSuccess(data.auth, authActions.passkeyLoginCompleteSuccess),
      authActions.setToken(data.auth, authActions.passkeyLoginCompleteSuccess.type)
    ])
  ));

  // Cancel passkey login ceremony
  onCancelPasskeyLogin$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.cancelPasskeyLogin),
    switchMap(() => from(import('@simplewebauthn/browser')).pipe(
      tap(({ WebAuthnAbortService }) => {
        WebAuthnAbortService.cancelCeremony();
      }),
      mergeMap(() => [
        zefRemoveProgress(authActions.passkeyLoginStart.type),
        zefRemoveProgress(authActions.manualPasskeyLogin.type)
      ])
    ))
  ));

  // # Passkey Registration Effects (post-registration flow)

  #validateWebAuthnRegistrationOptions(registrationOptions: any) {
    if (!registrationOptions) {
      return {
        valid: false as const,
        error: {
          message: 'Invalid WebAuthn response from server: missing registrationOptions',
          code: 'WEBAUTHN_INVALID_RESPONSE',
          status: 500
        }
      };
    }

    const hasChallenge = registrationOptions.challenge || registrationOptions.publicKey?.challenge;
    if (!hasChallenge) {
      return {
        valid: false as const,
        error: {
          message: 'Invalid WebAuthn response from server: missing challenge field',
          code: 'WEBAUTHN_MISSING_CHALLENGE',
          status: 500
        }
      };
    }

    return { valid: true as const };
  }

  onRegisterWithPasskey$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.registerWithPasskey),
    switchMap((action) => {
      const { sessionId, deviceName, registrationOptions } = action.data;

      const validation = this.#validateWebAuthnRegistrationOptions(registrationOptions);
      if (validation.valid === false) {
        return of(authActions.registerWithPasskeyFail(validation.error as any, action));
      }

      return performWebAuthnRegistration({
        registrationOptions,
        timeout: 60000
      }).pipe(
        switchMap((webAuthnResponse) =>
          this.#api.webAuthnRegistrationFinish$({
            sessionId,
            deviceName,
            response: webAuthnResponse
          }).pipe(
            map((apiResponse) => authActions.registerWithPasskeySuccess(apiResponse, action)),
            catchError((error) => of(authActions.registerWithPasskeyFail(error, action)))
          )
        ),
        catchError((error) => {
          return of(authActions.registerWithPasskeyFail(error, action));
        })
      );
    })
  ));

  onRegisterWithPasskeySuccess$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.registerWithPasskeySuccess),
    map(({ data }) => authActions.setToken(this.#mergeIntoStoredToken(data.auth), authActions.registerWithPasskeySuccess.type))
  ));

  // # 2FA Management Effects

  loadCredentials$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.loadCredentials),
    switchMap((action) =>
      this.#api.credentialsList$().pipe(
        map((response) => authActions.loadCredentialsSuccess(response, action)),
        catchError((error) => of(authActions.loadCredentialsFailure(error, action)))
      )
    )
  ));

  deleteCredential$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.deleteCredential),
    switchMap((action) =>
      this.#api.credentialDelete$(action.data.id).pipe(
        map(() => authActions.deleteCredentialSuccess({ id: action.data.id }, action)),
        catchError((error) => of(authActions.deleteCredentialFailure(error, action)))
      )
    )
  ));

  startTOTPSetup$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.startTOTPSetup),
    switchMap((action) =>
      this.#api.totpRegistrationStart$().pipe(
        map((response) => authActions.startTOTPSetupSuccess(response, action)),
        catchError((error) => of(authActions.startTOTPSetupFailure(error, action)))
      )
    )
  ));

  finishTOTPSetup$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.finishTOTPSetup),
    switchMap((action) =>
      this.#api.totpRegistrationFinish$(action.data).pipe(
        map((tokenData) => authActions.finishTOTPSetupSuccess(tokenData, action)),
        catchError((error) => of(authActions.finishTOTPSetupFailure(error, action)))
      )
    )
  ));

  removeTOTP$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.removeTOTP),
    switchMap((action) =>
      this.#api.totpRemove$().pipe(
        map(() => authActions.removeTOTPSuccess(undefined, action)),
        catchError((error) => of(authActions.removeTOTPFailure(error, action)))
      )
    )
  ));

  startWebAuthnSetup$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.startWebAuthnSetup),
    switchMap((action) =>
      this.#api.webAuthnRegistrationStart$(action.data.residentKey).pipe(
        map((response) => authActions.startWebAuthnSetupSuccess(response, action)),
        catchError((error) => of(authActions.startWebAuthnSetupFailure(error, action)))
      )
    )
  ));

  finishWebAuthnSetup$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.finishWebAuthnSetup),
    switchMap((action) =>
      this.#api.webAuthnRegistrationFinish$(action.data).pipe(
        map((response) => authActions.finishWebAuthnSetupSuccess(response, action)),
        catchError((error) => of(authActions.finishWebAuthnSetupFailure(error, action)))
      )
    )
  ));

  loadRecoveryCodes$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.loadRecoveryCodes),
    switchMap((action) =>
      this.#api.recoveryCodesGet$().pipe(
        map((response) => authActions.loadRecoveryCodesSuccess(response, action)),
        catchError((error) => of(authActions.loadRecoveryCodesFailure(error, action)))
      )
    )
  ));

  generateRecoveryCodes$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.generateRecoveryCodes),
    switchMap((action) =>
      this.#api.recoveryCodesGenerate$().pipe(
        map((response) => authActions.generateRecoveryCodesSuccess(response, action)),
        catchError((error) => of(authActions.generateRecoveryCodesFailure(error, action)))
      )
    )
  ));

  // Update token after TOTP setup and reload credentials
  onFinishTOTPSetupUpdateToken$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.finishTOTPSetupSuccess),
    mergeMap(({ data }) => [
      authActions.setToken(this.#mergeIntoStoredToken(data), authActions.finishTOTPSetupSuccess.type),
      authActions.loadCredentials()
    ])
  ));

  // Update token after WebAuthn setup and reload credentials
  onFinishWebAuthnSetupUpdateToken$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.finishWebAuthnSetupSuccess),
    mergeMap(({ data }) => [
      authActions.setToken(this.#mergeIntoStoredToken(data.auth), authActions.finishWebAuthnSetupSuccess.type),
      authActions.loadCredentials()
    ])
  ));

  // # Password Management Effects
  onChangePassword$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.changePassword),
    switchMap((action) =>
      this.#api.changePassword$(action.data).pipe(
        mergeMap((response) => [
          authActions.changePasswordSuccess(response, action),
          loadUser()
        ]),
        catchError((error) => of(authActions.changePasswordFailure(error, action)))
      )
    )
  ));

  onChangePasswordSuccessFormReset$ = createEffect(() => this.#actions$.pipe(
    ofType(authActions.changePasswordSuccess),
    mergeMap(() => [
      this.#passwordChangeForm.reset(),
      this.#passwordChangeForm.setDefaultValues()
    ])
  ));

  // # Sudo Error Handling
  //
  // Centralized effect that listens to ALL actions and detects sudo errors.
  // This effect runs alongside the standard failure action flow:
  // 1. API call fails → standard *Failure action dispatched
  // 2. onAnySudoError$ filters ANY action with SUDO_ERROR_CODE
  // 3. Dispatches zefAddError with SUDO_ERROR_KEY for UI handling
  onAnySudoError$ = createEffect(() => this.#actions$.pipe(
    filter((action: any) => isSudoRequiredError(action?.meta?.zefError)),
    map((action: any) => {
      const error = action.meta.zefError as ZefHttpErrorResponse;
      return zefAddError(
        SUDO_ERROR_KEY,
        error.code,
        error.message || 'Sudo mode required',
        undefined,
        error.originalError,
        'dialog',
        action.originalAction
      );
    })
  ));

  ngrxOnInitEffects() {
    return { type: this.#initType };
  }

  /** The 2FA setup endpoints update the current access token in place but return it without the refresh token. */
  #mergeIntoStoredToken(data: TokenData): TokenData {
    return { ...this.#tokenStorage.getToken(), ...data };
  }
}
